import React, { useEffect, useState } from "react";
import { all_routes } from "../../router/all_routes";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import EmpSuccess from "../employees/employeeSuccess";
import "../employees/index.css";
import { createDirectorsLoanCapital, getDirectorsLoanCapitalById, updateLoanCapitalById } from "../../../apis/directors";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { Moment } from "moment";
import PredefinedDateRanges from "../../../core/common/datePicker";
import { errorAlert } from "../../../style/scss/utils/Alerts";


const initialValues = {
    capital: "",
    directorId: "",
    loanAmount: "",
    loanInterestRate: "",
    loanDate: "",
};

// paymentDetails has historically been saved both as a plain array and as a
// JSON-stringified array (sometimes double-encoded, since the JSON column
// type re-serializes whatever it's handed). This normalizes any of those
// shapes into a real array so every read path agrees on the same data.
const parsePaymentDetails = (raw: unknown): any[] => {
    let value: unknown = raw;
    let iterations = 0;
    while (typeof value === "string" && iterations < 5) {
        const trimmed = value.trim();
        if (!trimmed || trimmed === "null") return [];
        try {
            value = JSON.parse(trimmed);
        } catch {
            return [];
        }
        iterations += 1;
    }
    return Array.isArray(value) ? value : [];
};

const parseJsonArray = (raw: unknown): any[] => {
    let value: unknown = raw;
    for (let iteration = 0; typeof value === "string" && iteration < 5; iteration += 1) {
        try {
            value = JSON.parse(value);
        } catch {
            return [];
        }
    }
    return Array.isArray(value) ? value : [];
};

const formatInterestBalance = (amount: number): string => {
    const absoluteAmount = Math.abs(amount).toFixed(2);
    return amount < 0 ? `(-₹ ${absoluteAmount})` : `₹ ${absoluteAmount}`;
};

type CapitalResponse = {
    id: number;
    directorId: number;
    capital: number;
    loanAmount: number;
    loanInterestRate: number;
    loanDate: string;
    paymentDetails: string; // JSON string
    interestRateHistory?: unknown;
};
type CapitalData = {
    key: number;
    directorId: number;
    capital: number;
    loanAmount: number;
    loanInterestRate: number;
    loanDate: string;
    paymentDetails: string;
    interestRateHistory?: unknown;
    id: number;
};



const DirectorCapital = () => {
    const { search } = useLocation();
    const id = new URLSearchParams(search).get("id");
    const userInfo = JSON.parse(sessionStorage.getItem("info") || "{}");
    const financerId = userInfo.financer_id ?? userInfo.id;

    const [allCapitalData, setCapitalData] = useState<CapitalData[]>([]);
    const [successCreation, setSuccessCreation] = useState<boolean>(false);
    const [formData, setFormData] = useState(initialValues);
    const [selectedLoan, setSelectedLoan] = useState<any>(null);
    const [payments, setPayments] = useState<any[]>([]);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
    const [showAddCapitalModal, setShowAddCapitalModal] = useState(false);
    const [showRateModal, setShowRateModal] = useState(false);
    const [rateError, setRateError] = useState("");
    const [rateForm, setRateForm] = useState({ effectiveDate: "", rate: "" });
    const [paymentError, setPaymentError] = useState("");
    const [paymentForm, setPaymentForm] = useState({
        amount: "",
        paymentType: "interest",
        date_time: "",
    });

    const handlePaymentChange = (e) => {
        const { name, value } = e.target;
        setPaymentError("");
        setPaymentForm({ ...paymentForm, [name]: value });
    };


    const [states, setState] = useState<{
        start: Moment | undefined;
        end: Moment | undefined;
    }>({
        start: undefined,
        end: undefined,
    });
    const { start, end } = states;


    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const calculateInterest = (
        loanAmount: number,
        rate: number,
        loanDate: string,
        paymentDetails?: unknown,
        interestRateHistory?: unknown
    ) => {
        if (!loanAmount || !Number.isFinite(Number(rate)) || !loanDate) return 0;
        const loanStart = moment(loanDate).startOf("day");
        const calculationEnd = (end ? moment(end) : moment()).startOf("day");
        if (!loanStart.isValid() || calculationEnd.isSameOrBefore(loanStart)) {
            return 0;
        }

        const principalPayments = parsePaymentDetails(paymentDetails)
            .filter(
                (payment: any) =>
                    ["loan", "principal"].includes(payment?.paymentType) &&
                    payment?.date_time &&
                    Number(payment?.amount) > 0
            )
            .map((payment: any) => ({
                amount: Number(payment.amount),
                date: moment(payment.date_time).startOf("day"),
            }))
            .filter(
                (payment: any) =>
                    payment.date.isValid() && payment.date.isAfter(loanStart)
            )
            .sort((a: any, b: any) => a.date.valueOf() - b.date.valueOf());

        const rateChanges = parseJsonArray(interestRateHistory)
            .map((change: any) => ({
                rate: Number(change?.rate),
                date: moment(change?.effectiveDate).startOf("day"),
            }))
            .filter((change: any) =>
                Number.isFinite(change.rate) && change.rate >= 0 &&
                change.date.isValid() && change.date.isAfter(loanStart)
            );

        const events = [
            ...principalPayments.map((payment: any) => ({ ...payment, type: "payment" })),
            ...rateChanges.map((change: any) => ({ ...change, type: "rate" })),
        ].sort((a: any, b: any) => a.date.valueOf() - b.date.valueOf());

        let outstandingPrincipal = Number(loanAmount);
        let currentRate = Number(rate);
        let periodStart = loanStart.clone();
        let accruedInterest = 0;

        for (const event of events) {
            if (event.date.isAfter(calculationEnd)) {
                break;
            }

            const days = event.date.diff(periodStart, "days");
            if (days > 0 && outstandingPrincipal > 0) {
                accruedInterest +=
                    (outstandingPrincipal * currentRate * days) / (100 * 365);
            }

            if (event.type === "payment") {
                outstandingPrincipal = Math.max(outstandingPrincipal - event.amount, 0);
            } else {
                currentRate = event.rate;
            }
            periodStart = event.date.clone();
        }

        if (outstandingPrincipal > 0 && calculationEnd.isAfter(periodStart)) {
            const remainingDays = calculationEnd.diff(periodStart, "days");
            accruedInterest +=
                (outstandingPrincipal * currentRate * remainingDays) / (100 * 365);
        }

        return accruedInterest;
    };

    const getTotalInterest = () => {
        return allCapitalData.reduce((total, item) => {
            return (
                total +
                calculateInterest(
                    item.loanAmount,
                    item.loanInterestRate,
                    item.loanDate,
                    item.paymentDetails,
                    item.interestRateHistory
                )
            );
        }, 0);
    };

    const fetchAllCapital = async () => {
        try {
            const response = await getDirectorsLoanCapitalById(id, financerId);

            let capitalData = response?.data || [];
            if (start && end) {
                capitalData = capitalData.filter((item: CapitalResponse) => {
                    const loanDate = moment(item.loanDate);
                    return loanDate.isBetween(start, end, "day", "[]");
                });
            }

            const formatted = capitalData.map((item: CapitalResponse, i: number) => ({
                key: i + 1,
                directorId: item.directorId,
                capital: item.capital,
                loanAmount: item.loanAmount,
                loanInterestRate: item.loanInterestRate,
                loanDate: item.loanDate,
                paymentDetails: item.paymentDetails,
                interestRateHistory: item.interestRateHistory,
                id: item.id,
            }));

            setCapitalData(formatted);
        } catch (error) {
            console.error("Error fetching capital data:", error);
        }
    };

    const getTotalLoan = () => {
        return allCapitalData.reduce((total, item) => {
            return total + Number(item.loanAmount || 0);
        }, 0);
    };

    const getTotalCapital = () => {
        return allCapitalData.reduce((total, item) => {
            return total + Number(item.capital || 0);
        }, 0);
    };

    const handleViewPayments = (record: any) => {
        setSelectedLoan(record);
        setPayments(parsePaymentDetails(record.paymentDetails));
        setShowPaymentModal(true);
    };

    useEffect(() => {
        if (id) {
            fetchAllCapital();
        }
    }, [states, id]);

    const handleAddPayment = (record: any) => {
        setSelectedLoan(record);
        setPaymentError("");
        setShowAddPaymentModal(true);
    };

    const getPaidInterest = (paymentDetails: unknown): number => {
        // Use the gross amount, not the post-TDS netAmount: TDS is withheld
        // and deposited to the government on the payee's behalf, so the full
        // interest obligation is discharged even though only netAmount is
        // disbursed as cash. Using netAmount here understated Paid Interest
        // (and overstated Remaining Interest) by the TDS cut on every payment.
        return parsePaymentDetails(paymentDetails)
            .filter((payment: any) => payment?.paymentType === "interest")
            .reduce(
                (sum: number, payment: any) => sum + (Number(payment?.amount) || 0),
                0
            );
    };

    const getRemainingLoan = (record: CapitalData) => {
        const payments = parsePaymentDetails(record.paymentDetails);

        // Payment type dropdown only ever saves "loan" (never "principal"),
        // so filtering on "principal" silently dropped every repayment and
        // left Remaining Loan permanently equal to Total Loan.
        const paidPrincipal = payments
            .filter((p: any) => p?.paymentType === "loan")
            .reduce((sum: number, p: any) => sum + Number(p?.amount || 0), 0);

        return Math.max(Number(record.loanAmount) - paidPrincipal, 0);
    };

    const getBalanceInterest = (record: CapitalData) => {
        const totalInterest = calculateInterest(
            record.loanAmount,
            record.loanInterestRate,
            record.loanDate,
            record.paymentDetails,
            record.interestRateHistory
        );

        const paidInterest = getPaidInterest(record.paymentDetails);

        return totalInterest - paidInterest;
    };

    const getRateSchedule = (record: CapitalData) => [
        { effectiveDate: record.loanDate, rate: Number(record.loanInterestRate), initial: true },
        ...parseJsonArray(record.interestRateHistory),
    ].sort((a: any, b: any) => moment(a.effectiveDate).valueOf() - moment(b.effectiveDate).valueOf());

    const getCurrentRate = (record: CapitalData) => {
        const calculationDate = (end ? moment(end) : moment()).startOf("day");
        return getRateSchedule(record).reduce(
            (activeRate: number, change: any) =>
                moment(change.effectiveDate).startOf("day").isSameOrBefore(calculationDate)
                    ? Number(change.rate)
                    : activeRate,
            Number(record.loanInterestRate)
        );
    };

    const handleManageRates = (record: CapitalData) => {
        setSelectedLoan(record);
        setRateForm({ effectiveDate: "", rate: "" });
        setRateError("");
        setShowRateModal(true);
    };

    const saveRateHistory = async (history: any[]) => {
        await updateLoanCapitalById(selectedLoan.id, { interestRateHistory: history });
        setSelectedLoan({ ...selectedLoan, interestRateHistory: history });
        await fetchAllCapital();
    };

    const handleSaveRate = async () => {
        const newRate = Number(rateForm.rate);
        const effectiveDate = moment(rateForm.effectiveDate).startOf("day");
        const loanDate = moment(selectedLoan?.loanDate).startOf("day");
        if (!rateForm.effectiveDate || !Number.isFinite(newRate) || newRate < 0) {
            setRateError("Enter a valid effective date and interest rate.");
            return;
        }
        if (!effectiveDate.isAfter(loanDate)) {
            setRateError("The new rate date must be after the original loan date.");
            return;
        }

        const history = parseJsonArray(selectedLoan.interestRateHistory)
            .filter((change: any) => !moment(change.effectiveDate).isSame(effectiveDate, "day"));
        history.push({ effectiveDate: effectiveDate.format("YYYY-MM-DD"), rate: newRate });
        history.sort((a: any, b: any) => moment(a.effectiveDate).valueOf() - moment(b.effectiveDate).valueOf());

        try {
            await saveRateHistory(history);
            setRateForm({ effectiveDate: "", rate: "" });
            setRateError("");
        } catch (error) {
            console.error("Interest rate update failed", error);
            setRateError("Could not save the interest rate change.");
        }
    };

    const handleDeleteRate = async (effectiveDate: string) => {
        try {
            const history = parseJsonArray(selectedLoan.interestRateHistory)
                .filter((change: any) => change.effectiveDate !== effectiveDate);
            await saveRateHistory(history);
        } catch (error) {
            console.error("Interest rate deletion failed", error);
            setRateError("Could not remove the interest rate change.");
        }
    };

    const getTotalPaidInterest = () => {
        return allCapitalData.reduce(
            (total, item) => total + getPaidInterest(item.paymentDetails),
            0
        );
    };

    const getTotalBalanceInterest = () => {
        // The summary is a company-wide balance. Interest payments can cover
        // interest accrued across multiple director-loan entries, so clamping
        // every row before summing incorrectly discards any excess payment on
        // an individual row.
        return getTotalInterest() - getTotalPaidInterest();
    };

    const getTotalRemainingLoan = () => {
        return allCapitalData.reduce(
            (total, item) => total + getRemainingLoan(item),
            0
        );
    };

    const columns = [
        {
            title: "S.No",
            dataIndex: "key",
        },
        {
            title: "Date",
            dataIndex: "loanDate",
            render: (loanDate: string) => moment(loanDate).format("DD-MM-YYYY"),
        },
        {
            title: "Capital",
            dataIndex: "capital",
        },
        {
            title: "Loan Amount",
            dataIndex: "loanAmount",
        },
        {
            title: "Current Interest (%)",
            render: (_: any, record: CapitalData) => getCurrentRate(record),
        },
        {
            title: "Total Interest",
            dataIndex: "interest",
            render: (_: any, record: CapitalData) => {
                const interest = calculateInterest(
                    record.loanAmount,
                    record.loanInterestRate,
                    record.loanDate,
                    record.paymentDetails,
                    record.interestRateHistory
                );
                return interest;
            },
        },
        {
            title: "Paid Interest",
            render: (_: any, record: CapitalData) => {
                return getPaidInterest(record.paymentDetails);
            },
        },
        {
            title: "Balance Interest",
            render: (_: any, record: CapitalData) => {
                return formatInterestBalance(getBalanceInterest(record));
            },
        },
        {
            title: "Remaining Loan",
            render: (_: any, record: CapitalData) => {
                return `₹ ${getRemainingLoan(record).toFixed(2)}`;
            },
        },
        {
            title: "Actions",
            dataIndex: "actions",
            render: (_: any, record: CapitalData) => (
                <div className="d-flex gap-2">
                    <button
                        className="btn btn-sm btn-info"
                        onClick={() => handleViewPayments(record)}
                    >
                        View Payments
                    </button>

                    <button
                        className="btn btn-sm btn-success"
                        onClick={() => handleAddPayment(record)}
                    >
                        Add Payment
                    </button>
                    <button
                        className="btn btn-sm btn-warning"
                        onClick={() => handleManageRates(record)}
                    >
                        Manage ROI
                    </button>
                </div>
            ),
        }
    ];

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const payload = {
            directorId: Number(id), // from URL
            financer_id: financerId,
            capital: Number(formData.capital),
            loanAmount: Number(formData.loanAmount),
            loanInterestRate: Number(formData.loanInterestRate),
            loanDate: formData.loanDate,
        };

        try {
            const response = await createDirectorsLoanCapital(payload, financerId);
            if (response.status === true) {
                setSuccessCreation(true);
                setShowAddCapitalModal(false);
                fetchAllCapital();
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getAllPayments = () => {
        let allPayments: any[] = [];

        allCapitalData.forEach((loan) => {
            try {
                const payments = loan.paymentDetails
                    ? JSON.parse(loan.paymentDetails)
                    : [];

                payments.forEach((p: any, index: number) => {
                    allPayments.push({
                        key: `${loan.id}-${index}`,
                        loanId: loan.id,
                        date: p.date_time,
                        type: p.paymentType,
                        amount: p.amount,
                    });
                });
            } catch (err) {
                console.error("Invalid JSON", err);
            }
        });

        return allPayments;
    };

    const paymentColumns = [
        {
            title: "S.No",
            render: (_: any, __: any, index: number) => index + 1,
        },
        {
            title: "Loan ID",
            dataIndex: "loanId",
        },
        {
            title: "Date",
            dataIndex: "date",
            render: (date: string) => moment(date).format("DD-MM-YYYY"),
        },
        {
            title: "Type",
            dataIndex: "type",
        },
        {
            title: "Amount",
            dataIndex: "amount",
            render: (amt: number) => `₹ ${amt}`,
        },
    ];

    const handleSavePayment = async () => {
        if (!selectedLoan) return;

        let amount = Number(paymentForm.amount || 0);
        if (amount <= 0 || !paymentForm.date_time) {
            setPaymentError("Enter a valid amount and payment date.");
            errorAlert("Invalid payment", "Enter a valid amount and payment date.");
            return;
        }

        const remainingLoan = getRemainingLoan(selectedLoan);
        if (paymentForm.paymentType === "loan" && remainingLoan <= 0) {
            setPaymentError("This loan is already fully repaid.");
            errorAlert("Loan fully repaid", "No further loan payment is allowed.");
            return;
        }

        if (paymentForm.paymentType === "loan" && amount > remainingLoan) {
            const validationMessage = `Payment cannot exceed the remaining loan of ₹ ${remainingLoan.toFixed(2)}.`;
            setPaymentError(validationMessage);
            errorAlert(
                "Payment exceeds remaining loan",
                validationMessage
            );
            return;
        }

        let tds = 0;
        let netAmount = amount;

        // ✅ TDS only for interest
        if (paymentForm.paymentType === "interest") {
            tds = (amount * 10) / 100;
            netAmount = amount - tds;
        }

        const newPayment = {
            amount,
            paymentType: paymentForm.paymentType,
            date_time: paymentForm.date_time,
            tds,
            netAmount,
        };

        const existingPayments = parsePaymentDetails(selectedLoan.paymentDetails);
        const updatedPayments = [...existingPayments, newPayment];

        // Send the array as-is: paymentDetails is a Sequelize JSON column, so
        // the backend already serializes it. Pre-stringifying here made it
        // get JSON.stringify'd a second time on save, double- (and on repeat
        // edits, triple-) encoding the column until it could no longer be
        // parsed back into an array — which is why Paid Interest/Remaining
        // Loan silently showed 0 for affected loans.
        const payload = {
            paymentDetails: updatedPayments,
        };

        try {
            await updateLoanCapitalById(selectedLoan.id, payload);

            // ✅ refresh data from backend
            fetchAllCapital();

            setShowAddPaymentModal(false);

            setPaymentForm({
                amount: "",
                paymentType: "interest",
                date_time: "",
            });
            setPaymentError("");
        } catch (error) {
            console.error("Payment update failed", error);
        }
    };

    const handleViewAllPayments = () => {
        const allPayments: any[] = [];

        allCapitalData.forEach((record: any) => {
            const parsed = parsePaymentDetails(record.paymentDetails);
            allPayments.push(
                ...parsed.map((p: any) => ({
                    ...p,
                    loanId: record.id || "-",
                    loanAmount: record.loanAmount || 0,
                }))
            );
        });

        // Sort latest first
        allPayments.sort(
            (a, b) =>
                new Date(b.date_time || b.date).getTime() -
                new Date(a.date_time || a.date).getTime()
        );

        setPayments(allPayments);
        setShowPaymentModal(true);
    };
    return (
        <>
            {/* Page Wrapper */}
            <div className="page-wrapper">
                <div className="content">
                    {/* Breadcrumb */}
                    <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                        <div className="my-auto mb-2"></div>
                        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
                            <div className="mb-2">
                                <button
                                    className="btn btn-primary"
                                    onClick={() => {
                                        console.log("Clicked");
                                        setShowAddCapitalModal(true);
                                    }}
                                >
                                    + Add Capital / Loan
                                </button>
                                <button
                                    className="btn btn-primary ms-2"
                                    onClick={handleViewAllPayments}
                                >
                                    View All Payments
                                </button>
                            </div>
                            <div className="head-icons ms-2">
                                <CollapseHeader />
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                                <div className="me-3">
                                    <div>
                                        <label style={{ color: "#000" }}>Search</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm me-2"
                                            placeholder="Search by Name\Mobile"
                                            style={{
                                                backgroundColor: "#b4b4b447",
                                                padding: "0.2rem 0.85rem",
                                                marginTop: "5px",
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="input-icon-end position-relative">
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "5px",
                                        }}
                                    >
                                        <label style={{ color: "#000" }}>Date Range</label>
                                        <PredefinedDateRanges state={states} setState={setState} />
                                    </div>
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        margin: "20px 0px 0px 6rem",
                                    }}
                                >
                                    <div className="dropdown me-3">
                                        <Link
                                            style={{
                                                background: "#3ab54bbf",
                                                color: "#fff",
                                            }}
                                            to="#"
                                            className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                                            data-bs-toggle="dropdown"
                                        >
                                            Export
                                        </Link>

                                        <ul className="dropdown-menu  dropdown-menu-end p-3">
                                            <li>
                                                <Link to="#" className="dropdown-item rounded-1">
                                                    <i className="ti ti-file-type-pdf me-1" />
                                                    Export as PDF
                                                </Link>
                                            </li>
                                            <li>
                                                <Link to="#" className="dropdown-item rounded-1">
                                                    <i className="ti ti-file-type-xls me-1" />
                                                    Export as Excel{" "}
                                                </Link>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card-body pt-20">
                            <div className="row mb-3">
                                <div className="col-md-4" style={{ paddingBottom: 10 }}>
                                    <div
                                        style={{
                                            background: "linear-gradient(135deg, #2196F3, #1565C0)",
                                            borderRadius: "12px",
                                            padding: "15px",
                                            color: "#fff",
                                            boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                                        }}
                                    >
                                        <h6 style={{ opacity: 0.8 }}>Total Capital</h6>
                                        <h3 style={{ fontWeight: "bold", margin: 0 }}>
                                            ₹ {getTotalCapital().toFixed(2)}
                                        </h3>
                                    </div>
                                </div>

                                {/* Total Loan */}
                                <div className="col-md-4">
                                    <div
                                        style={{
                                            background: "linear-gradient(135deg, #FF9800, #EF6C00)",
                                            borderRadius: "12px",
                                            padding: "15px",
                                            color: "#fff",
                                            boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                                        }}
                                    >
                                        <h6 style={{ opacity: 0.8 }}>Total Loan</h6>
                                        <h3 style={{ fontWeight: "bold", margin: 0 }}>
                                            ₹ {getTotalLoan().toFixed(2)}
                                        </h3>
                                    </div>
                                </div>

                                {/* Total Interest */}
                                <div className="col-md-4">
                                    <div
                                        style={{
                                            background: "linear-gradient(135deg, #4CAF50, #2E7D32)",
                                            borderRadius: "12px",
                                            padding: "15px",
                                            color: "#fff",
                                            boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                                        }}
                                    >
                                        <h6 style={{ opacity: 0.8 }}>
                                            Total Interest {start && end && "(Filtered)"}
                                        </h6>
                                        <h3 style={{ fontWeight: "bold", margin: 0 }}>
                                            ₹ {getTotalInterest().toFixed(2)}
                                        </h3>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div style={{ background: "#673AB7", borderRadius: "12px", padding: "15px", color: "#fff" }}>
                                        <h6>Paid Interest</h6>
                                        <h3>₹ {getTotalPaidInterest().toFixed(2)}</h3>
                                    </div>
                                </div>

                                {/* Balance Interest */}
                                <div className="col-md-4">
                                    <div style={{ background: "#E91E63", borderRadius: "12px", padding: "15px", color: "#fff" }}>
                                        <h6>Remaining Interest</h6>
                                        <h3>{formatInterestBalance(getTotalBalanceInterest())}</h3>
                                    </div>
                                </div>

                                {/* Remaining Loan */}
                                <div className="col-md-4">
                                    <div style={{ background: "#009688", borderRadius: "12px", padding: "15px", color: "#fff" }}>
                                        <h6>Remaining Loan</h6>
                                        <h3>₹ {getTotalRemainingLoan().toFixed(2)}</h3>
                                    </div>
                                </div>
                            </div>
                            {allCapitalData && allCapitalData.length > 0 && (
                                <Table
                                    dataSource={allCapitalData}
                                    columns={columns}
                                    Selection={true}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {showPaymentModal && (
                <div
                    className="modal show d-block"
                    style={{ background: "rgba(0,0,0,0.5)" }}
                >
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Payment Details</h5>
                                <button
                                    className="btn-close"
                                    onClick={() => setShowPaymentModal(false)}
                                />
                            </div>

                            <div className="modal-body">
                                <table className="table table-bordered">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Date</th>
                                            <th>Type</th>
                                            <th>Amount</th>
                                            <th>TDS</th>
                                            <th>Net</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.isArray(payments) && payments.length > 0 ? (
                                            payments.map((p, i) => (
                                                <tr key={i}>
                                                    <td>{i + 1}</td>
                                                    <td>{moment(p.date_time).format("DD-MM-YYYY")}</td>
                                                    <td>{p.paymentType}</td>
                                                    <td>₹ {p.amount}</td>
                                                    <td>{p.paymentType === "interest" ? `₹ ${p.tds || 0}` : "-"}</td>
                                                    <td>
                                                        {p.paymentType === "interest"
                                                            ? `₹ ${p.netAmount || (p.amount - (p.tds || 0))}`
                                                            : `₹ ${p.amount}`}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={6} className="text-center">
                                                    No Payments Found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showAddPaymentModal && (
                <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-md modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5>Add Payment</h5>
                                <button
                                    className="btn-close"
                                    onClick={() => setShowAddPaymentModal(false)}
                                />
                            </div>

                            <div className="modal-body">
                                {/* <input type="number" placeholder="Amount" className="form-control mb-2" />
                                <select className="form-control mb-2">
                                    <option value="interest">Interest</option>
                                    <option value="principal">Principal</option>
                                </select>
                                <input type="date" className="form-control" /> */}
                                <input
                                    type="number"
                                    name="amount"
                                    value={paymentForm.amount}
                                    onChange={handlePaymentChange}
                                    max={
                                        paymentForm.paymentType === "loan" && selectedLoan
                                            ? getRemainingLoan(selectedLoan)
                                            : undefined
                                    }
                                    placeholder="Amount"
                                    className="form-control mb-2"
                                />

                                {paymentForm.paymentType === "loan" && selectedLoan && (
                                    <small className="d-block text-muted mb-2">
                                        Remaining loan: ₹ {getRemainingLoan(selectedLoan).toFixed(2)}
                                    </small>
                                )}

                                <select
                                    name="paymentType"
                                    value={paymentForm.paymentType}
                                    onChange={handlePaymentChange}
                                    className="form-control mb-2"
                                >
                                    <option value="capital">Capital</option>
                                    <option value="loan">Loan</option>
                                    <option value="interest">Interest</option>
                                </select>

                                <input
                                    type="date"
                                    name="date_time"
                                    value={paymentForm.date_time}
                                    onChange={handlePaymentChange}
                                    className="form-control"
                                />
                                {paymentError && (
                                    <div className="text-danger mt-2" role="alert">
                                        {paymentError}
                                    </div>
                                )}
                                {paymentForm.paymentType === "interest" && paymentForm.amount && (
                                    <div
                                        style={{
                                            background: "#f8f9fa",
                                            border: "1px solid #ddd",
                                            borderRadius: "8px",
                                            padding: "10px",
                                            marginTop: "10px",
                                        }}
                                    >
                                        {(() => {
                                            const amount = Number(paymentForm.amount || 0);
                                            const tds = (amount * 10) / 100;
                                            const net = amount - tds;

                                            return (
                                                <>
                                                    <div className="d-flex justify-content-between">
                                                        <span>Gross Amount</span>
                                                        <strong>₹ {amount.toFixed(2)}</strong>
                                                    </div>

                                                    <div className="d-flex justify-content-between text-danger">
                                                        <span>TDS (10%)</span>
                                                        <strong>₹ {tds.toFixed(2)}</strong>
                                                    </div>

                                                    <div className="d-flex justify-content-between text-success">
                                                        <span>Net Payable</span>
                                                        <strong>₹ {net.toFixed(2)}</strong>
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>
                                )}


                            </div>
                            <div>
                                <button className="btn btn-primary" onClick={handleSavePayment}>
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>

                </div>
            )}

            {showRateModal && selectedLoan && (
                <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)", zIndex: 9999 }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5>Manage Interest Rate</h5>
                                <button className="btn-close" onClick={() => setShowRateModal(false)} />
                            </div>
                            <div className="modal-body">
                                <p className="text-muted">
                                    Add each new ROI with the date it becomes effective. Previous periods remain unchanged.
                                </p>
                                <table className="table table-bordered mb-4">
                                    <thead>
                                        <tr>
                                            <th>Effective From</th>
                                            <th>ROI (%)</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getRateSchedule(selectedLoan).map((change: any, index: number) => (
                                            <tr key={`${change.effectiveDate}-${index}`}>
                                                <td>{moment(change.effectiveDate).format("DD-MM-YYYY")}</td>
                                                <td>{Number(change.rate)}</td>
                                                <td>
                                                    {change.initial ? (
                                                        <span className="text-muted">Original rate</span>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => handleDeleteRate(change.effectiveDate)}
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label>Effective From *</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            min={moment(selectedLoan.loanDate).add(1, "day").format("YYYY-MM-DD")}
                                            value={rateForm.effectiveDate}
                                            onChange={(event) => {
                                                setRateError("");
                                                setRateForm({ ...rateForm, effectiveDate: event.target.value });
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label>New Interest Rate (%) *</label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            className="form-control"
                                            value={rateForm.rate}
                                            onChange={(event) => {
                                                setRateError("");
                                                setRateForm({ ...rateForm, rate: event.target.value });
                                            }}
                                        />
                                    </div>
                                </div>
                                {rateError && <div className="text-danger" role="alert">{rateError}</div>}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowRateModal(false)}>
                                    Close
                                </button>
                                <button className="btn btn-primary" onClick={handleSaveRate}>
                                    Add Rate Change
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showAddCapitalModal && (
                <div
                    className="modal show d-block"
                    style={{ background: "rgba(0,0,0,0.5)", zIndex: 9999 }}
                >
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">

                            {/* Header */}
                            <div className="modal-header">
                                <h5>Add Capital / Loan</h5>
                                <button
                                    className="btn-close"
                                    onClick={() => setShowAddCapitalModal(false)}
                                />
                            </div>

                            {/* Body */}
                            <div className="modal-body">
                                <div className="row">

                                    {/* Capital */}
                                    <div className="col-md-6 mb-3">
                                        <label>Capital *</label>
                                        <input
                                            type="number"
                                            name="capital"
                                            value={formData.capital}
                                            onChange={handleChange}
                                            placeholder="Enter Capital"
                                            className="form-control"
                                        />
                                    </div>

                                    {/* Loan Amount */}
                                    <div className="col-md-6 mb-3">
                                        <label>Loan Amount *</label>
                                        <input
                                            type="number"
                                            name="loanAmount"
                                            value={formData.loanAmount}
                                            onChange={handleChange}
                                            placeholder="Enter Loan Amount"
                                            className="form-control"
                                        />
                                    </div>

                                    {/* Loan Date */}
                                    <div className="col-md-6 mb-3">
                                        <label>Loan Date *</label>
                                        <input
                                            type="date"
                                            name="loanDate"
                                            value={formData.loanDate}
                                            onChange={handleChange}
                                            className="form-control"
                                        />
                                    </div>

                                    {/* Interest */}
                                    <div className="col-md-6 mb-3">
                                        <label>Loan Interest Rate (%) *</label>
                                        <input
                                            type="number"
                                            name="loanInterestRate"
                                            value={formData.loanInterestRate}
                                            onChange={handleChange}
                                            placeholder="Enter Interest Rate"
                                            className="form-control"
                                        />
                                    </div>

                                </div>
                            </div>

                            {/* Footer */}
                            <div className="modal-footer">
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => setShowAddCapitalModal(false)}
                                >
                                    Cancel
                                </button>

                                <button
                                    className="btn btn-primary"
                                    onClick={(event) => handleSubmit(event as any)}
                                >
                                    Save
                                </button>
                            </div>

                        </div>
                    </div>
                </div>
            )}
            {successCreation && <EmpSuccess all_routes={all_routes} />}
        </>
    );
};

export default DirectorCapital;
